/*
 * BBVT - Pepper Patron $5 checkout guard
 *
 * Prevents the $5 Pepper Patron Give Only variant from starting
 * checkout when it is the only item in the cart.
 *
 * The existing BoomGate checkout validation remains the server-side
 * fallback for attempts that bypass the storefront cart.
 */

(() => {
  const BLOCKED_SKU = 'PPGiveOnly';
  const BLOCKED_VARIANT_TITLE = '$5';

  const ERROR_MESSAGE =
    "The $5 Pepper Patron contribution can't be purchased by itself. Please choose a higher contribution amount or add another item to your cart.";

  const CHECKOUT_FORM_IDS = new Set([
    'cart',
    'CartDrawer-Form',
    'cart-notification-form'
  ]);

  let cartRequest = null;

  function getCart() {
    if (cartRequest) return cartRequest;

    const root =
      window.Shopify && window.Shopify.routes && window.Shopify.routes.root
        ? window.Shopify.routes.root
        : '/';

    cartRequest = fetch(`${root}cart.js`, {
      method: 'GET',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json'
      }
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Unable to read cart.');
        }

        return response.json();
      })
      .finally(() => {
        cartRequest = null;
      });

    return cartRequest;
  }

  function isBlockedCart(cart) {
    if (
      !cart ||
      cart.item_count !== 1 ||
      !Array.isArray(cart.items) ||
      cart.items.length !== 1
    ) {
      return false;
    }

    const item = cart.items[0];

    if (item.quantity !== 1 || item.sku !== BLOCKED_SKU) {
      return false;
    }

    const variantTitle = String(item.variant_title || '').trim();

    const variantOptions = Array.isArray(item.variant_options)
      ? item.variant_options.map((option) => String(option).trim())
      : [];

    /*
     * Primary identifier: the $5 variant title/option.
     *
     * original_price === 500 is included as a fallback for the
     * store's current USD configuration and remains unaffected
     * by the FIVE discount.
     */
    return (
      variantTitle === BLOCKED_VARIANT_TITLE ||
      variantOptions.includes(BLOCKED_VARIANT_TITLE) ||
      item.original_price === 500
    );
  }

  function getCheckoutButton(form) {
    if (!form) return null;

    if (form.id === 'CartDrawer-Form') {
      return document.getElementById('CartDrawer-Checkout');
    }

    if (form.id === 'cart-notification-form') {
      return form.querySelector('[name="checkout"]');
    }

    return document.getElementById('checkout');
  }

  function getMessageId(form) {
    if (form && form.id === 'CartDrawer-Form') {
      return 'PPGiveOnly-CheckoutGuard-Drawer';
    }

    if (form && form.id === 'cart-notification-form') {
      return 'PPGiveOnly-CheckoutGuard-Notification';
    }

    return 'PPGiveOnly-CheckoutGuard-Cart';
  }

  function showBlockedMessage(form) {
    const button = getCheckoutButton(form);

    if (!button) return;

    const messageId = getMessageId(form);
    let message = document.getElementById(messageId);

    if (!message) {
      message = document.createElement('div');
      message.id = messageId;
      message.className =
        'alert alert--error alert--small ppgiveonly-checkout-guard-message';
      message.setAttribute('role', 'alert');
      message.setAttribute('tabindex', '-1');
      message.textContent = ERROR_MESSAGE;

      const ctas = button.closest('.cart__ctas');

      if (ctas && ctas.parentNode) {
        ctas.parentNode.insertBefore(message, ctas);
      } else {
        button.parentNode.insertBefore(message, button);
      }
    }

    message.classList.remove('hidden');

    message.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest'
    });

    try {
      message.focus({ preventScroll: true });
    } catch (error) {
      message.focus();
    }
  }

  function hideBlockedMessages() {
    document
      .querySelectorAll('.ppgiveonly-checkout-guard-message')
      .forEach((message) => {
        message.classList.add('hidden');
      });
  }

  function setAcceleratedCheckoutVisibility(blocked) {
    document
      .querySelectorAll(
        '.cart__dynamic-checkout-buttons, .additional-checkout-buttons'
      )
      .forEach((container) => {
        container.hidden = blocked;
      });
  }

  function syncCartPageState(cart) {
    const blocked = isBlockedCart(cart);

    setAcceleratedCheckoutVisibility(blocked);

    if (!blocked) {
      hideBlockedMessages();
    }
  }

  /*
   * Intercept normal cart/drawer checkout submission before Shopify
   * receives the request that starts checkout.
   */
  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;

      if (
        !(form instanceof HTMLFormElement) ||
        !CHECKOUT_FORM_IDS.has(form.id)
      ) {
        return;
      }

      const submitter = event.submitter;

      if (submitter && submitter.name && submitter.name !== 'checkout') {
        return;
      }

      if (form.dataset.ppgiveonlyCheckoutBypass === 'true') {
        delete form.dataset.ppgiveonlyCheckoutBypass;
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();

      getCart()
        .then((cart) => {
          syncCartPageState(cart);

          if (isBlockedCart(cart)) {
            showBlockedMessage(form);
            return;
          }

          form.dataset.ppgiveonlyCheckoutBypass = 'true';

          if (
            submitter &&
            typeof form.requestSubmit === 'function'
          ) {
            form.requestSubmit(submitter);
          } else {
            HTMLFormElement.prototype.submit.call(form);
          }
        })
        .catch(() => {
          /*
           * Fail open if Shopify's cart endpoint is temporarily
           * unavailable so legitimate customers aren't trapped.
           */
          form.dataset.ppgiveonlyCheckoutBypass = 'true';

          if (
            submitter &&
            typeof form.requestSubmit === 'function'
          ) {
            form.requestSubmit(submitter);
          } else {
            HTMLFormElement.prototype.submit.call(form);
          }
        });
    },
    true
  );

  /*
   * Keep accelerated checkout buttons off the cart page when this
   * exact blocked cart is present.
   */
  function refreshGuardState() {
    getCart()
      .then(syncCartPageState)
      .catch(() => {
        setAcceleratedCheckoutVisibility(false);
      });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', refreshGuardState);
  } else {
    refreshGuardState();
  }

  /*
   * Woodstock already emits this after cart quantity changes.
   */
  document.addEventListener('afterUpdateQuantity', (event) => {
    const cart =
      event.detail && event.detail.cartData
        ? event.detail.cartData
        : null;

    if (cart) {
      syncCartPageState(cart);
    } else {
      refreshGuardState();
    }
  });
})();